/**
 * FoodPulse Admin — one-page dashboard for site operators.
 *
 * Layout: left sidebar with 7 tabs (Overview, Users, Businesses,
 * Products, Deals, Claims, Submissions, Reports) + a right-side main
 * pane. Every tab lazy-loads its own data on click, so the initial
 * paint stays fast even with 60k+ businesses.
 *
 * Auth: relies on the shared `useAuth` hook — non-admins get a
 * "Access denied" screen. All API calls hit `/api/foodpulse/admin/*`
 * which are `current_admin` gated on the backend.
 *
 * Route: /food-pulse/admin
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, Building2, ChartLine, ChefHat, Coins, FileText,
  Loader2, MessageSquare, Search, Store, Tags, Users as UsersIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "overview",    label: "Overview",     icon: ChartLine },
  { key: "users",       label: "Users",        icon: UsersIcon },
  { key: "businesses",  label: "Businesses",   icon: Building2 },
  { key: "products",    label: "Products",     icon: Store },
  { key: "deals",       label: "Deals",        icon: Tags },
  { key: "claims",      label: "Claims",       icon: FileText },
  { key: "submissions", label: "Submissions",  icon: ChefHat },
  { key: "community",   label: "Community",    icon: MessageSquare },
  { key: "reports",     label: "Reports",      icon: Coins },
];

// Token is auto-attached by AuthContext via axios.defaults.headers, so
// we just need the URL prefix here.
async function apiGet(path, _token, params = {}) {
  const res = await axios.get(`${API}/api/foodpulse/admin${path}`, { params });
  return res.data;
}

// ─────────────────── KPI card ──────────────────────────────────────────
function Kpi({ label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>
        {label}
      </div>
      <div className="mt-1 font-display text-3xl leading-none" style={{ color: "#1A1613" }}>
        {(value ?? 0).toLocaleString()}
      </div>
      {hint && <div className="mt-1 text-[11px] opacity-70" style={{ color: "#3E2A0A" }}>{hint}</div>}
    </div>
  );
}

// ─────────────────── Data table shell ─────────────────────────────────
function DataTable({ columns, rows, loading, empty }) {
  if (loading) {
    return (
      <div className="p-12 text-center text-sm opacity-70">
        <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> Loading…
      </div>
    );
  }
  if (!rows?.length) {
    return <div className="p-12 text-center text-sm opacity-70">{empty || "No records found."}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-2xl border bg-white" style={{ borderColor: "#E9D8B4" }}>
      <table className="w-full text-sm">
        <thead className="text-[10px] font-mono uppercase tracking-widest" style={{ background: "#FBF3E0", color: "#8B5E10" }}>
          <tr>{columns.map((c) => (
            <th key={c.key} className="text-left px-3 py-2 whitespace-nowrap">{c.label}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.slug || i} className="border-t" style={{ borderColor: "#F0E2C7" }}>
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 align-top" style={{ color: "#3E2A0A" }}>
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ skip, limit, total, onPage }) {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between mt-3 text-xs" style={{ color: "#3E2A0A" }}>
      <div className="opacity-70">
        {total.toLocaleString()} total · page {page} / {pages}
      </div>
      <div className="flex gap-2">
        <button className="px-3 py-1 rounded border disabled:opacity-40" style={{ borderColor: "#E9D8B4" }}
                disabled={page <= 1} onClick={() => onPage(Math.max(0, skip - limit))}
                data-testid="admin-prev-page">← Prev</button>
        <button className="px-3 py-1 rounded border disabled:opacity-40" style={{ borderColor: "#E9D8B4" }}
                disabled={page >= pages} onClick={() => onPage(skip + limit)}
                data-testid="admin-next-page">Next →</button>
      </div>
    </div>
  );
}

/** Download an admin table as a CSV via the streaming export endpoint. */
function ExportCsvButton({ resource, q }) {
  const download = () => {
    const url = new URL(`${API}/api/foodpulse/admin/export/${resource}.csv`);
    if (q) url.searchParams.set("q", q);
    url.searchParams.set("limit", "5000");
    // Anchor download — auth header travels via cookies/axios defaults
    // aren't attached to native <a> clicks, so we go through fetch and
    // build a blob URL. Keeps the Authorization Bearer intact.
    (async () => {
      try {
        const res = await axios.get(url.toString(), { responseType: "blob" });
        const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
        const dl = document.createElement("a");
        dl.href = window.URL.createObjectURL(blob);
        dl.download = `foodpulse-${resource}.csv`;
        document.body.appendChild(dl);
        dl.click();
        dl.remove();
        toast.success(`Exported ${resource} to CSV`);
      } catch { toast.error("Couldn't export — check your permissions."); }
    })();
  };
  return (
    <button onClick={download}
            className="px-3 py-2 rounded-lg border text-xs font-mono uppercase tracking-wider hover:bg-white"
            style={{ borderColor: "#E9D8B4", color: "#3E2A0A" }}
            data-testid={`admin-export-${resource}`}
            title={`Download ${resource} as CSV`}>
      ⬇ Export CSV
    </button>
  );
}

// ─────────────────── Overview tab ─────────────────────────────────────
function OverviewTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiGet("/overview", token).then((d) => { if (alive) setData(d); })
      .catch(() => toast.error("Couldn't load overview"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  if (loading || !data) return <div className="p-8 text-sm opacity-70 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading KPIs…</div>;

  const artByCat = Object.entries(data.articles?.by_category || {})
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Users</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Total" value={data.users.total} testid="kpi-users-total" />
          <Kpi label="New · 7d" value={data.users.new_7d} testid="kpi-users-7d" />
          <Kpi label="New · 30d" value={data.users.new_30d} testid="kpi-users-30d" />
          <Kpi label="Verified" value={data.users.verified} testid="kpi-users-verified" />
          <Kpi label="Admins" value={data.users.admins} testid="kpi-users-admin" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Businesses & Brands</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Businesses" value={data.businesses.total} testid="kpi-biz-total" />
          <Kpi label="Claimed" value={data.businesses.claimed}
               hint={`${data.businesses.total ? Math.round(100 * data.businesses.claimed / data.businesses.total) : 0}% of listings`}
               testid="kpi-biz-claimed" />
          <Kpi label="Pending claims" value={data.businesses.pending_claims} testid="kpi-biz-pending" />
          <Kpi label="Brands" value={data.brands.total} testid="kpi-brands-total" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Content coverage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Food items" value={data.food_items.total}
               hint={`${data.food_items.draft} draft · ${data.food_items.curated} curated`}
               testid="kpi-items-total" />
          <Kpi label="Recipes" value={data.recipes.total}
               hint={`${data.recipes.no_instructions} missing steps`}
               testid="kpi-recipes-total" />
          <Kpi label="Articles" value={data.articles.total}
               hint={artByCat.slice(0, 3).map(([k, v]) => `${k}:${v}`).join(" · ")}
               testid="kpi-articles-total" />
          <Kpi label="Health articles"
               value={data.articles.by_category?.health || 0}
               testid="kpi-articles-health" />
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Marketplace</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Products" value={data.products.total} testid="kpi-products-total" />
          <Kpi label="Deals" value={data.deals.total} testid="kpi-deals-total" />
          <Kpi label="Submissions" value={data.submissions.total} testid="kpi-submissions-total" />
          <Kpi label="Total claims" value={data.claims.total}
               hint={`${data.claims.otp_pending} pending · ${data.claims.verified} verified`}
               testid="kpi-claims-total" />
        </div>
      </section>
    </div>
  );
}

// ─────────────────── Reusable paginated tab shell ─────────────────────
function usePagedTable(path, token, extraParams = {}) {
  const [state, setState] = useState({ items: [], total: 0, skip: 0, limit: 25, loading: true });
  const paramsKey = JSON.stringify(extraParams);
  const load = useCallback((skip = 0) => {
    setState((s) => ({ ...s, loading: true }));
    apiGet(path, token, { ...extraParams, skip, limit: state.limit })
      .then((d) => setState((s) => ({ ...s, ...d, loading: false })))
      .catch(() => { toast.error("Load failed"); setState((s) => ({ ...s, loading: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, token, paramsKey]);
  useEffect(() => { load(0); }, [load]);
  return { ...state, reload: load };
}

// ─────────────────── Users tab ────────────────────────────────────────
function UsersTab({ token }) {
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState("");
  const [isAdmin, setIsAdmin] = useState("");
  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (verified) p.verified = verified === "true";
    if (isAdmin) p.is_admin = isAdmin === "true";
    return p;
  }, [q, verified, isAdmin]);
  const t = usePagedTable("/users", token, params);

  const cols = [
    { key: "name",   label: "Name",     render: (r) => <div>
        <div className="font-medium" style={{ color: "#1A1613" }}>{r.name || "—"}</div>
        <div className="text-[11px] opacity-60">@{r.handle || "—"}</div>
      </div> },
    { key: "email",  label: "Email",    render: (r) => <span className="font-mono text-[12px]">{r.email}</span> },
    { key: "role",   label: "Role",     render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider">
        {r.is_admin ? "admin" : (r.role || "user")}
      </span> },
    { key: "email_verified", label: "Verified",
      render: (r) => r.email_verified ? "✓" : "—" },
    { key: "city",   label: "City",     render: (r) => `${r.city || ""}${r.country ? ", " + r.country : ""}` },
    { key: "created_at", label: "Signed up",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div data-testid="admin-users-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search email, name, handle or phone…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
                 style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-users-search" />
        </div>
        <select value={verified} onChange={(e) => setVerified(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-users-verified">
          <option value="">All verify states</option>
          <option value="true">Verified only</option>
          <option value="false">Unverified only</option>
        </select>
        <select value={isAdmin} onChange={(e) => setIsAdmin(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-users-role">
          <option value="">All roles</option>
          <option value="true">Admins only</option>
        </select>
        <ExportCsvButton resource="users" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No users match your filters." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Businesses tab ───────────────────────────────────
function BusinessesTab({ token }) {
  const [q, setQ] = useState("");
  const [claim, setClaim] = useState("");
  const [state, setState] = useState("");
  const params = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (claim) p.claim_status = claim;
    if (state) p.state_code = state.toUpperCase();
    return p;
  }, [q, claim, state]);
  const t = usePagedTable("/businesses", token, params);

  const cols = [
    { key: "name", label: "Business", render: (r) =>
        <Link to={`/food-pulse/businesses/${r.slug}`} className="hover:underline" style={{ color: "#8B5E10" }}
              data-testid={`biz-link-${r.slug}`}>{r.name}</Link> },
    { key: "business_type", label: "Type", render: (r) => r.business_type || "—" },
    { key: "state_code", label: "State", render: (r) => `${r.city || ""} ${r.state_code || ""}` },
    { key: "claim_status", label: "Claim",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: r.claim_status === "claimed" ? "#DCFCE7"
                                                : r.claim_status === "pending" ? "#FEF3C7" : "#F3F4F6",
                                     color:      r.claim_status === "claimed" ? "#166534"
                                                : r.claim_status === "pending" ? "#92400E" : "#374151" }}>
          {r.claim_status || "unclaimed"}
        </span> },
    { key: "likes_count", label: "Likes", render: (r) => r.likes_count || 0 },
    { key: "created_at", label: "Added",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];

  return (
    <div data-testid="admin-businesses-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, slug or city…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-biz-search" />
        </div>
        <select value={claim} onChange={(e) => setClaim(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-biz-claim">
          <option value="">All claim states</option>
          <option value="claimed">Claimed</option>
          <option value="pending">Pending</option>
          <option value="unclaimed">Unclaimed</option>
        </select>
        <input value={state} onChange={(e) => setState(e.target.value)}
               placeholder="State (e.g. NY)" maxLength={2}
               className="w-24 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
               data-testid="admin-biz-state" />
        <ExportCsvButton resource="businesses" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No businesses match your filters." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Products / Deals / Claims / Submissions ──────────
function ProductsTab({ token }) {
  const [q, setQ] = useState("");
  const params = useMemo(() => q ? { q } : {}, [q]);
  const t = usePagedTable("/products", token, params);
  const cols = [
    { key: "name", label: "Product" },
    { key: "business_name", label: "Business" },
    { key: "price", label: "Price", render: (r) => r.price != null ? `${r.currency || ""}${r.price}` : "—" },
    { key: "stock", label: "Stock", render: (r) => r.stock ?? "—" },
    { key: "is_active", label: "Active", render: (r) => r.is_active ? "✓" : "—" },
    { key: "created_at", label: "Added", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];
  return (
    <div data-testid="admin-products-tab">
      <div className="relative mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-2.5 opacity-40" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search product or business name…"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                 data-testid="admin-products-search" />
        </div>
        <ExportCsvButton resource="products" q={q} />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No products yet — try inviting sellers to list." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

function DealsTab({ token }) {
  const t = usePagedTable("/deals", token, {});
  const cols = [
    { key: "title", label: "Deal" },
    { key: "business_name", label: "Business" },
    { key: "price_original", label: "Was" },
    { key: "price_deal", label: "Now" },
    { key: "status", label: "Status" },
    { key: "created_at", label: "Posted", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
  ];
  return (
    <div data-testid="admin-deals-tab">
      <div className="flex justify-end mb-3"><ExportCsvButton resource="deals" /></div>
      <DataTable columns={cols} rows={t.items} loading={t.loading}
                 empty="No deals posted yet. When sellers post surplus / clearance offers they will appear here." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

function ClaimReviewModal({ claim, onClose, onDecided }) {
  const [audit, setAudit] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-businesses/claim/${claim.id}/audit`);
        if (!cancelled) setAudit(res.data);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load audit trail");
      }
    })();
    return () => { cancelled = true; };
  }, [claim.id]);

  const decide = async (action) => {
    if (action === "reject" && !notes.trim()) {
      if (!window.confirm("Reject without a note? The owner won't know why.")) return;
    }
    setBusy(action);
    try {
      const res = await axios.post(
        `${API}/api/food-businesses/claim/${claim.id}/decide`,
        { action, notes: notes.trim() || null });
      toast.success(`${action.replace("_", " ")} · owner ${res.data.email_status === "sent" ? "notified" : "not emailed (check Resend)"}`);
      onDecided?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't ${action}`);
    } finally {
      setBusy("");
    }
  };

  const proofs = audit?.claim?.proofs || claim.proofs || [];
  const trail  = audit?.trail || [];

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="admin-claim-review-modal">
      <div className="bg-white w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10"
             style={{ borderColor: "#E9D8B4" }}>
          <div>
            <div className="text-xs font-mono uppercase tracking-wider opacity-70">Claim review</div>
            <div className="font-semibold text-lg" style={{ color: "#3E2A0A" }}>
              {claim.business_name || claim.business_slug}
            </div>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"
                  data-testid="admin-claim-review-close">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Claimant */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-60">Claimant</div>
              <div className="font-medium">{claim.claimant_name}</div>
              <div className="text-[11px] font-mono opacity-70">{claim.claimant_email}</div>
              <div className="text-[11px] font-mono opacity-70">{claim.claimant_role}{claim.claimant_phone ? " · " + claim.claimant_phone : ""}</div>
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-60">Status</div>
              <div className="font-medium">{claim.status}</div>
              <div className="text-[11px] font-mono opacity-70">Submitted {new Date(claim.created_at).toLocaleString()}</div>
              {claim.verified_at && (
                <div className="text-[11px] font-mono opacity-70">OTP verified {new Date(claim.verified_at).toLocaleString()}</div>
              )}
            </div>
          </div>

          {claim.message && (
            <div className="text-sm rounded-lg p-3" style={{ background: "#FEF9C3", border: "1px solid #FDE68A" }}>
              <div className="text-[11px] font-mono uppercase opacity-70 mb-1">Message from claimant</div>
              {claim.message}
            </div>
          )}

          {/* Proofs */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mb-2">
              Uploaded proofs ({proofs.length})
            </div>
            {proofs.length === 0 ? (
              <div className="text-xs opacity-60 border rounded-lg p-3"
                   style={{ borderColor: "#E9D8B4", background: "#FDF6E3" }}>
                No proof uploaded yet — consider "Request more info" instead of rejecting.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {proofs.map((p) => {
                  const url = `${API}/api/food-businesses/claim/proof/${p.path}`;
                  const isImg = (p.content_type || "").startsWith("image/");
                  return (
                    <a key={p.id} href={url} target="_blank" rel="noreferrer"
                       className="block rounded-lg border overflow-hidden hover:shadow"
                       style={{ borderColor: "#E9D8B4" }}
                       data-testid={`admin-claim-proof-${p.id}`}>
                      {isImg ? (
                        <img src={url} alt={p.filename}
                             className="w-full aspect-square object-cover" />
                      ) : (
                        <div className="w-full aspect-square flex items-center justify-center text-[11px] font-mono opacity-60"
                             style={{ background: "#FDF6E3" }}>
                          PDF · view →
                        </div>
                      )}
                      <div className="px-1.5 py-1 text-[10px] flex items-center justify-between bg-white">
                        <span className="uppercase tracking-wider opacity-70">{p.kind}</span>
                        <span className="opacity-50">{Math.round((p.size || 0) / 1024)} KB</span>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Corrections owner suggested */}
          {claim.corrections && Object.keys(claim.corrections).some((k) => claim.corrections[k]) && (
            <div>
              <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mb-1">
                Owner-suggested corrections
              </div>
              <div className="text-xs rounded-lg p-2 space-y-0.5"
                   style={{ background: "#FDF6E3", border: "1px solid #E9D8B4" }}>
                {Object.entries(claim.corrections).filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><b className="opacity-70">{k.replace("correction_", "")}:</b> {String(v)}</div>
                ))}
              </div>
            </div>
          )}

          {/* Audit trail */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mb-1">
              Audit trail
            </div>
            <div className="rounded-lg border divide-y text-xs"
                 style={{ borderColor: "#E9D8B4" }}>
              {trail.length === 0 && <div className="p-2 opacity-60">No events yet.</div>}
              {trail.map((r, i) => (
                <div key={i} className="p-2 flex items-start gap-2">
                  <div className="font-mono text-[10px] opacity-60 w-24 shrink-0">
                    {new Date(r.ts).toLocaleString()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
                            style={{ background: r.action.includes("approved") ? "#DCFCE7" : r.action.includes("rejected") ? "#FEE2E2" : "#F3F4F6",
                                     color:      r.action.includes("approved") ? "#166534" : r.action.includes("rejected") ? "#991B1B" : "#374151" }}>
                        {r.action}
                      </span>
                      <span className="text-[10px] opacity-70">by {r.actor}</span>
                    </div>
                    {r.notes && <div className="mt-0.5 opacity-80">{r.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decision form */}
          {claim.status !== "approved" && claim.status !== "rejected" && (
            <div className="rounded-lg border p-3" style={{ borderColor: "#E9D8B4", background: "#FDF6E3" }}>
              <label className="text-[11px] font-mono uppercase tracking-wider opacity-60 block mb-1">
                Decision note (visible to the owner in email)
              </label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                        rows={3} maxLength={2000}
                        placeholder="What did you verify — or what's missing?"
                        data-testid="admin-claim-review-notes"
                        className="w-full rounded-lg border px-2.5 py-1.5 text-sm bg-white"
                        style={{ borderColor: "#E9D8B4" }} />
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                <button onClick={() => decide("request_more_info")} disabled={!!busy}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                        style={{ background: "#B45309" }}
                        data-testid="admin-claim-review-more-info">
                  {busy === "request_more_info" ? "…" : "Request more info"}
                </button>
                <button onClick={() => decide("reject")} disabled={!!busy}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                        style={{ background: "#991B1B" }}
                        data-testid="admin-claim-review-reject">
                  {busy === "reject" ? "…" : "Reject"}
                </button>
                <button onClick={() => decide("approve")} disabled={!!busy}
                        className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                        style={{ background: "#166534" }}
                        data-testid="admin-claim-review-approve">
                  {busy === "approve" ? "…" : "Approve · Grant ✓ badge"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClaimsTab({ token }) {
  const [status, setStatus] = useState("");
  const [reviewing, setReviewing] = useState(null);   // full claim doc
  const params = useMemo(() => status ? { status } : {}, [status]);
  const t = usePagedTable("/claims", token, params);

  const cols = [
    { key: "business_name", label: "Business",
      render: (r) => <Link to={`/food-pulse/businesses/${r.business_slug}`} className="hover:underline" style={{ color: "#8B5E10" }}>
          {r.business_name || r.business_slug}
        </Link> },
    { key: "claimant_name", label: "Claimant",
      render: (r) => <div>
        <div className="font-medium">{r.claimant_name}</div>
        <div className="text-[11px] font-mono opacity-70">{r.claimant_email}</div>
      </div> },
    { key: "proofs", label: "Proofs",
      render: (r) => <span className="text-xs font-mono px-2 py-0.5 rounded"
                            style={{ background: (r.proofs || []).length ? "#DCFCE7" : "#F3F4F6",
                                     color:      (r.proofs || []).length ? "#166534" : "#6B7280" }}>
          {(r.proofs || []).length} file{(r.proofs || []).length !== 1 ? "s" : ""}
        </span> },
    { key: "status", label: "Status",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                            style={{ background: r.status === "verified" || r.status === "approved" ? "#DCFCE7"
                                                : r.status === "otp_pending" ? "#FEF3C7"
                                                : r.status === "rejected" ? "#FEE2E2" : "#F3F4F6",
                                     color:      r.status === "verified" || r.status === "approved" ? "#166534"
                                                : r.status === "otp_pending" ? "#92400E"
                                                : r.status === "rejected" ? "#991B1B" : "#374151" }}>
          {r.status}
        </span> },
    { key: "created_at", label: "Submitted",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
    { key: "actions", label: "",
      render: (r) => (
        <button onClick={() => setReviewing(r)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white"
                style={{ background: "#8B5E10" }}
                data-testid={`claim-review-${r.id}`}>
          Review →
        </button>
      ) },
  ];
  return (
    <div data-testid="admin-claims-tab">
      <div className="flex items-center gap-3 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-claims-status">
          <option value="">All statuses</option>
          <option value="otp_pending">Pending OTP</option>
          <option value="verified">Verified (awaiting badge review)</option>
          <option value="approved">Approved ✓</option>
          <option value="rejected">Rejected</option>
        </select>
        <ExportCsvButton resource="claims" />
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No claims yet." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
      {reviewing && (
        <ClaimReviewModal claim={reviewing}
                          onClose={() => setReviewing(null)}
                          onDecided={() => t.reload(t.skip)} />
      )}
    </div>
  );
}

function SubmissionsTab({ token }) {
  const [kind, setKind] = useState("all");
  const params = useMemo(() => ({ kind }), [kind]);
  const t = usePagedTable("/submissions", token, params);
  const cols = [
    { key: "_label", label: "Type",
      render: (r) => <span className="text-[11px] font-mono uppercase tracking-wider">{r._kind}</span> },
    { key: "name", label: "Subject",
      render: (r) => r.name || r.title || r.subject || r.slug || "—" },
    { key: "submitted_by_email", label: "Submitted by",
      render: (r) => r.submitted_by_email || r.email || "—" },
    { key: "created_at", label: "When",
      render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
  ];
  return (
    <div data-testid="admin-submissions-tab">
      <div className="flex items-center gap-3 mb-4">
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "#E9D8B4" }}
                data-testid="admin-subs-kind">
          <option value="all">All types</option>
          <option value="product">Product suggestions</option>
          <option value="recipe">Recipe suggestions</option>
          <option value="public">Public submissions</option>
          <option value="photo">Photo submissions</option>
        </select>
      </div>
      <DataTable columns={cols} rows={t.items} loading={t.loading} empty="No submissions yet." />
      <Pagination skip={t.skip} limit={t.limit} total={t.total} onPage={t.reload} />
    </div>
  );
}

// ─────────────────── Reports tab ──────────────────────────────────────
function Sparkline({ series, testid }) {
  if (!series?.length) return null;
  const w = 320, h = 44, pad = 4;
  const max = Math.max(1, ...series.map((p) => p.count));
  const xStep = (w - pad * 2) / Math.max(1, series.length - 1);
  const points = series.map((p, i) => [
    pad + i * xStep,
    h - pad - (p.count / max) * (h - pad * 2),
  ]);
  const d = "M " + points.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid={testid}>
      <path d={d} fill="none" stroke="#8B5E10" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.5" fill="#8B5E10" opacity="0.6" />
      ))}
    </svg>
  );
}

function ReportsTab({ token }) {
  const [growth, setGrowth] = useState(null);
  const [geo, setGeo] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [g, ge, c] = await Promise.all([
          apiGet("/reports/growth", token, { days: 30 }),
          apiGet("/reports/geo", token, { limit: 15 }),
          apiGet("/reports/content", token),
        ]);
        if (alive) { setGrowth(g); setGeo(ge); setContent(c); }
      } catch { toast.error("Report load failed"); }
      finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) return <div className="p-8 text-sm opacity-70 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Building reports…</div>;

  return (
    <div className="space-y-8" data-testid="admin-reports-tab">
      {growth && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>30-day growth</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["users", "New user signups"],
              ["businesses", "Businesses added"],
              ["claims", "Business claim submissions"],
              ["products", "Product listings"],
            ].map(([k, label]) => {
              const series = growth[k] || [];
              const total = series.reduce((a, p) => a + (p.count || 0), 0);
              return (
                <div key={k} className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={`report-growth-${k}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }}>{label}</div>
                    <div className="font-display text-lg" style={{ color: "#1A1613" }}>{total.toLocaleString()}</div>
                  </div>
                  <Sparkline series={series} testid={`spark-${k}`} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {geo && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Geographic coverage</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-states">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Top states</div>
              <ul className="space-y-1 text-sm">
                {geo.states?.map((s) => (
                  <li key={s.state_code} className="flex items-center justify-between">
                    <span className="font-mono">{s.state_code}</span>
                    <span className="opacity-70">{s.total.toLocaleString()} · {s.claimed_pct}% claimed</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-metros">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Top metros</div>
              <ul className="space-y-1 text-sm">
                {geo.metros?.map((m) => (
                  <li key={m.metro_area} className="flex items-center justify-between">
                    <span className="truncate mr-2">{m.metro_area}</span>
                    <span className="opacity-70">{m.total.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="report-geo-types">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#8B5E10" }}>Business types</div>
              <ul className="space-y-1 text-sm">
                {geo.types?.map((t) => (
                  <li key={t.business_type} className="flex items-center justify-between">
                    <span className="truncate mr-2">{t.business_type}</span>
                    <span className="opacity-70">{t.total.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {content && (
        <section>
          <h2 className="font-display text-2xl mb-3" style={{ color: "#1A1613" }}>Content coverage</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["food_items", "Food items"],
              ["recipes", "Recipes"],
              ["brands", "Brands"],
              ["businesses", "Businesses"],
            ].map(([k, label]) => {
              const c = content[k];
              const pct = c.curated_pct ?? 0;
              return (
                <div key={k} className="rounded-2xl border p-4 bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={`report-content-${k}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>{label}</div>
                  <div className="mt-1 font-display text-2xl" style={{ color: "#1A1613" }}>{pct}%</div>
                  <div className="text-[11px] opacity-70 mt-0.5">
                    {c.total?.toLocaleString?.() ?? c.total} total
                    {c.ai_seeded != null && <> · {c.ai_seeded} AI-seeded</>}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full" style={{ background: "#F0E2C7" }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#8B5E10" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

// ─────────────────── Activity strip (below sidebar on Overview) ───────
function ActivityStrip({ token }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    apiGet("/activity", token, { limit: 8 }).then((d) => setItems(d.items || [])).catch(() => {});
  }, [token]);
  if (!items.length) return null;
  return (
    <aside className="rounded-2xl border p-4 bg-white sticky top-4" style={{ borderColor: "#E9D8B4" }} data-testid="admin-activity">
      <div className="text-[10px] font-mono uppercase tracking-widest mb-3 flex items-center gap-1.5" style={{ color: "#8B5E10" }}>
        <Activity className="w-3.5 h-3.5" /> Recent activity
      </div>
      <ol className="space-y-3 text-sm">
        {items.map((e, i) => (
          <li key={i} className="pb-3 border-b last:border-b-0" style={{ borderColor: "#F0E2C7" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60" style={{ color: "#8B5E10" }}>
              {e.kind}
            </div>
            {e.link
              ? <Link to={e.link} className="hover:underline block" style={{ color: "#3E2A0A" }}>{e.title}</Link>
              : <div style={{ color: "#3E2A0A" }}>{e.title}</div>}
            {e.subtitle && <div className="text-[11px] opacity-70">{e.subtitle}</div>}
            <div className="text-[10px] opacity-50 mt-0.5">
              {e.at ? new Date(e.at).toLocaleString() : ""}
            </div>
          </li>
        ))}
      </ol>
    </aside>
  );
}

// ─────────────────── Root page ────────────────────────────────────────
export default function FoodPulseAdmin() {
  const { user, loading: authLoading } = useAuth() || {};
  const token = null; // axios auto-attaches via AuthContext defaults
  const [tab, setTab] = useState("overview");

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-sm opacity-60">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking access…
    </div>;
  }
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "#FBF7F0" }}>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>Restricted</div>
          <h1 className="font-display text-3xl mb-2" style={{ color: "#1A1613" }}>Admin access required</h1>
          <p className="text-sm opacity-80 mb-4" style={{ color: "#3E2A0A" }}>
            You need a FoodPulse admin account to view this dashboard.
          </p>
          <Link to="/food-pulse" className="text-sm underline" style={{ color: "#8B5E10" }} data-testid="admin-denied-home">
            ← Back to Food Pulse
          </Link>
        </div>
      </div>
    );
  }

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

// ─────────────────── Community moderation ──────────────────────────
function CommunityTab({ token: _token }) {
  const [subtab, setSubtab] = useState("feed"); // feed | reports
  const [feed, setFeed] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (subtab === "feed") {
        const url = `${API}/api/admin/community/feed`
          + (typeFilter ? `?post_type=${typeFilter}` : "");
        const r = await axios.get(url);
        setFeed(r.data?.items || []);
      } else {
        const r = await axios.get(`${API}/api/admin/community/reports?status=pending&limit=100`);
        setReports(r.data?.items || []);
      }
    } catch (err) {
      toast.error("Couldn't load — retry?");
    } finally {
      setLoading(false);
    }
  }, [subtab, typeFilter]);
  useEffect(() => { load(); }, [load]);

  const hidePost = async (id) => {
    if (!window.confirm("Hide this post from everyone?")) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/api/admin/community/posts/${id}/hide`);
      toast.success("Post hidden");
      setFeed((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { toast.error("Couldn't hide"); }
    finally { setBusyId(""); }
  };

  const decideReport = async (rid, action) => {
    const notes = window.prompt(
      `Notes for ${action.replace("_", " ")} (visible in audit only):`, "") || "";
    setBusyId(rid);
    try {
      await axios.post(`${API}/api/admin/community/reports/${rid}/decide`,
                       { action, notes: notes.trim() || null });
      toast.success(`Report ${action.replace("_", " ")}`);
      setReports((prev) => prev.filter((r) => r.id !== rid));
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't decide"); }
    finally { setBusyId(""); }
  };

  const TYPES = ["", "comment", "complaint", "suggestion", "abuse_report", "business_reply"];

  return (
    <div className="space-y-4" data-testid="admin-community-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setSubtab("feed")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${subtab === "feed" ? "text-white" : ""}`}
                style={subtab === "feed" ? { background: "#8B5E10" } : { background: "#FDF6E3", color: "#3E2A0A" }}
                data-testid="admin-community-tab-feed">
          Live feed ({feed.length})
        </button>
        <button onClick={() => setSubtab("reports")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${subtab === "reports" ? "text-white" : ""}`}
                style={subtab === "reports" ? { background: "#B91C1C" } : { background: "#FDF6E3", color: "#3E2A0A" }}
                data-testid="admin-community-tab-reports">
          🚩 Reports ({reports.length})
        </button>
        {subtab === "feed" && (
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="ml-2 text-xs px-2 py-1.5 rounded-lg border" style={{ borderColor: "#E9D8B4" }}
                  data-testid="admin-community-type-filter">
            {TYPES.map((t) => <option key={t || "all"} value={t}>{t || "All types"}</option>)}
          </select>
        )}
      </div>

      {loading && <div className="text-xs opacity-60"><Loader2 className="w-3 h-3 inline animate-spin mr-1"/>Loading…</div>}

      {!loading && subtab === "feed" && feed.map((m) => (
        <div key={m.id} className="rounded-xl border p-3 bg-white"
             style={{ borderColor: "#E9D8B4" }} data-testid={`admin-community-post-${m.id}`}>
          <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
            <span className="font-medium">{m.user_name}</span>
            <span className="opacity-60">on</span>
            <Link to={m.entity_type === "food_business" ? `/food-pulse/businesses/${m.entity_id}` :
                        m.entity_type === "city" ? `/food-pulse/city/${m.entity_id.split(":").join("/").replace(":", "/")}` :
                        "#"}
                  className="underline" style={{ color: "#8B5E10" }}>
              {m.entity_type}/{m.entity_id}
            </Link>
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded"
                  style={{ background: "#F3F4F6", color: "#374151" }}>{m.post_type}</span>
            {m.report_count > 0 && (
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded"
                    style={{ background: "#FEE2E2", color: "#991B1B" }}>
                🚩 {m.report_count} report{m.report_count > 1 ? "s" : ""}
              </span>
            )}
            <span className="opacity-50 ml-auto text-[11px]">{new Date(m.created_at).toLocaleString()}</span>
          </div>
          <div className="text-sm whitespace-pre-wrap">{m.body}</div>
          {Array.isArray(m.images) && m.images.length > 0 && (
            <div className="mt-2 flex gap-1 flex-wrap">
              {m.images.slice(0, 6).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer">
                  <img src={u} alt="" className="w-14 h-14 rounded object-cover border"/>
                </a>
              ))}
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <button onClick={() => hidePost(m.id)} disabled={busyId === m.id}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded text-white disabled:opacity-50"
                    style={{ background: "#991B1B" }}
                    data-testid={`admin-community-hide-${m.id}`}>
              {busyId === m.id ? "…" : "Hide post"}
            </button>
          </div>
        </div>
      ))}
      {!loading && subtab === "feed" && feed.length === 0 && (
        <div className="opacity-60 text-sm">No posts match — try a different filter.</div>
      )}

      {!loading && subtab === "reports" && reports.map((r) => (
        <div key={r.id} className="rounded-xl border p-3 bg-white"
             style={{ borderColor: "#E9D8B4" }} data-testid={`admin-community-report-${r.id}`}>
          <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded"
                  style={{ background: "#FEE2E2", color: "#991B1B" }}>
              {r.reason}
            </span>
            <span className="opacity-60">on {r.target_type}</span>
            <span className="font-mono opacity-70 truncate">{r.target_id}</span>
            <span className="opacity-50 ml-auto text-[11px]">{new Date(r.created_at).toLocaleString()}</span>
          </div>
          {r.notes && <div className="text-xs italic opacity-80 mb-1">"{r.notes}"</div>}
          {r.snapshot?.body && (
            <div className="text-sm rounded p-2" style={{ background: "#FDF6E3" }}>
              {r.snapshot.body}
            </div>
          )}
          <div className="text-[11px] opacity-70 mt-1">
            Reported by {r.reporter_name || r.reporter_email || "anonymous"}
          </div>
          <div className="mt-2 flex justify-end gap-1.5 flex-wrap">
            <button onClick={() => decideReport(r.id, "dismiss")} disabled={busyId === r.id}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded disabled:opacity-50"
                    style={{ background: "#F3F4F6", color: "#374151" }}
                    data-testid={`admin-community-dismiss-${r.id}`}>
              Dismiss
            </button>
            <button onClick={() => decideReport(r.id, "warn_user")} disabled={busyId === r.id}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded text-white disabled:opacity-50"
                    style={{ background: "#B45309" }}
                    data-testid={`admin-community-warn-${r.id}`}>
              Warn user
            </button>
            <button onClick={() => decideReport(r.id, "hide_post")} disabled={busyId === r.id}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded text-white disabled:opacity-50"
                    style={{ background: "#991B1B" }}
                    data-testid={`admin-community-hidepost-${r.id}`}>
              Hide post
            </button>
            <button onClick={() => decideReport(r.id, "ban_user")} disabled={busyId === r.id}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded text-white disabled:opacity-50"
                    style={{ background: "#7F1D1D" }}
                    data-testid={`admin-community-ban-${r.id}`}>
              Ban user
            </button>
          </div>
        </div>
      ))}
      {!loading && subtab === "reports" && reports.length === 0 && (
        <div className="opacity-60 text-sm">🎉 No pending reports.</div>
      )}
    </div>
  );
}

  const renderTab = () => {
    switch (tab) {
      case "overview":    return <OverviewTab   token={token} />;
      case "users":       return <UsersTab      token={token} />;
      case "businesses":  return <BusinessesTab token={token} />;
      case "products":    return <ProductsTab   token={token} />;
      case "deals":       return <DealsTab      token={token} />;
      case "claims":      return <ClaimsTab     token={token} />;
      case "submissions": return <SubmissionsTab token={token} />;
      case "community":   return <CommunityTab   token={token} />;
      case "reports":     return <ReportsTab    token={token} />;
      default:            return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="foodpulse-admin">
      {/* Header */}
      <header className="border-b" style={{ background: "#F0E2C7", borderColor: "#E9D8B4" }}>
        <div className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8B5E10" }}>
              — Food Pulse · admin
            </div>
            <h1 className="font-display text-3xl md:text-4xl leading-none mt-1" style={{ color: "#1A1613" }}>
              Operator dashboard
            </h1>
          </div>
          <Link to="/food-pulse" className="text-[11px] font-mono uppercase tracking-widest opacity-70 hover:opacity-100 hover:underline"
                style={{ color: "#3E2A0A" }} data-testid="admin-back-home">
            ← Site home
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 md:grid md:grid-cols-[220px_1fr_260px] md:gap-6">
        {/* Sidebar */}
        <nav className="mb-4 md:mb-0" data-testid="admin-sidebar">
          <div className="rounded-2xl border overflow-hidden bg-white sticky top-4" style={{ borderColor: "#E9D8B4" }}>
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                        data-testid={`admin-tab-${t.key}`}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left border-b last:border-b-0 transition-colors ${active ? "font-semibold" : ""}`}
                        style={{
                          borderColor: "#F0E2C7",
                          background: active ? "#8B5E10" : "transparent",
                          color:      active ? "white"   : "#3E2A0A",
                        }}>
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Main */}
        <main className="min-w-0">
          <div className="mb-4">
            <h2 className="font-display text-2xl" style={{ color: "#1A1613" }}>{activeTab.label}</h2>
          </div>
          {renderTab()}
        </main>

        {/* Right rail — activity feed */}
        <div className="hidden md:block">
          <ActivityStrip token={token} />
        </div>
      </div>
    </div>
  );
}
